/**
 * SuperAdmin sets localStorage on admin.* ; artist portal runs on artist.* — storage is not shared.
 * Admin also sets cookie `sf_impersonated_artist_id` on `.starfarm.com` and puts `artistId` in the redirect URL.
 * We persist URL/cookie into artist-origin localStorage so routes like /artist/dashboard still see the id.
 */

const IMPERSONATED_ARTIST_KEY = "impersonatedArtistId";
const IMPERSONATING_KEY = "impersonating";
const COOKIE_NAME = "sf_impersonated_artist_id";

export function isMongoIdString(value: string): boolean {
  return /^[a-fA-F0-9]{24}$/.test(value);
}

export function readArtistIdFromLocationSearch(search: string): string {
  try {
    const raw = new URLSearchParams(search).get("artistId");
    if (!raw) return "";
    const id = decodeURIComponent(raw).trim();
    return isMongoIdString(id) ? id : "";
  } catch {
    return "";
  }
}

/** Cross-subdomain cookie set by starfarm-admin SuperAdmin impersonation */
export function readImpersonatedArtistCookie(): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`)
  );
  if (!match?.[1]) return "";
  const v = decodeURIComponent(match[1].trim());
  return isMongoIdString(v) ? v : "";
}

/** Call on app load (e.g. TokenManager) so /dashboard?artistId= survives client-side navigation */
export function persistImpersonatedArtistIdFromUrlOrCookie(): void {
  if (typeof window === "undefined") return;
  const fromUrl = readArtistIdFromLocationSearch(window.location.search);
  if (fromUrl) {
    localStorage.setItem(IMPERSONATED_ARTIST_KEY, fromUrl);
    localStorage.setItem(IMPERSONATING_KEY, "true");
    return;
  }
  const existing = localStorage.getItem(IMPERSONATED_ARTIST_KEY) || "";
  if (existing && isMongoIdString(existing)) return;
  const fromCookie = readImpersonatedArtistCookie();
  if (fromCookie) {
    localStorage.setItem(IMPERSONATED_ARTIST_KEY, fromCookie);
    localStorage.setItem(IMPERSONATING_KEY, "true");
  }
}

export function resolvedImpersonationArtistId(
  search: string,
  storageId: string | null
): string {
  const fromUrl = readArtistIdFromLocationSearch(search);
  if (fromUrl) return fromUrl;
  const fromStorage = (storageId || "").trim();
  if (isMongoIdString(fromStorage)) return fromStorage;
  const fromCookie = readImpersonatedArtistCookie();
  return isMongoIdString(fromCookie) ? fromCookie : "";
}
